/**
 * Where the football on screen came from.
 *
 * The product shows two very different kinds of game side by side (real
 * NFL games and a scripted practice one) and the single worst thing it
 * could do is let someone confuse them. So every game carries a source,
 * and every source says plainly what it is.
 *
 * `verified: true` means one specific thing: **this is real data from a
 * named outside source.** It is not a claim that the source endorses this
 * product, and the copy is written so it cannot be read that way. The
 * practice game is `verified: false` and is never given a tick.
 */

export const DATA_SOURCES = Object.freeze({
  espn: {
    id: "espn",
    name: "ESPN",
    /** Shown under the mark, e.g. "Live from ESPN". */
    shortLabel: "ESPN",
    logo: "/logos/sources/espn.png",
    verified: true,
    describes: "Real NFL schedule, scores and play-by-play",
    /** Read out by screen readers and shown on hover. */
    title: "Real NFL data from ESPN, a verified source",
    url: "https://www.espn.com/nfl/",
  },

  /**
   * A real NFL game. The shield says "this is the actual NFL", which is
   * what a viewer wants to know at a glance; the notice above the list
   * names ESPN as the feed it arrived on.
   *
   * Note the wording: NOT "official NFL data". This is ESPN's data about
   * the NFL, not a league feed, and the difference matters.
   */
  nfl: {
    id: "nfl",
    name: "NFL",
    shortLabel: "NFL",
    logo: "/logos/sources/nfl.png",
    verified: true,
    describes: "Real NFL schedule, scores and play-by-play, via ESPN",
    title: "A real NFL game. Live data via ESPN.",
    url: "https://www.nfl.com/",
  },

  simulated: {
    id: "simulated",
    name: "Practice game",
    shortLabel: "Practice",
    logo: null,
    verified: false,
    describes: "A scripted simulation, not a real game",
    title: "A scripted practice game. Not real NFL data.",
    url: null,
  },
});

/**
 * Which source a game came from.
 *
 * Driven off the game record rather than a hardcoded provider name, so a
 * future vendor swap changes one map entry and nothing else.
 */
export function sourceForGame(game) {
  if (!game) return null;
  if (game.isSimulated) return DATA_SOURCES.simulated;
  return DATA_SOURCES[game.provider] ?? DATA_SOURCES.espn;
}

/**
 * Which source a player record came from.
 *
 * Separate from `sourceForGame` because the answer is different: a player
 * profile is ESPN's own database of people, not a league feed about a
 * match, and the ESPN entry's copy says exactly that. Badging a
 * biography with the NFL shield would claim a provenance it does not
 * have.
 *
 * Driven off the record's `provenance.source`, which the player
 * normaliser stamps, so this stays true if the profile feed ever changes
 * hands.
 */
export function sourceForPlayer(player) {
  if (!player) return null;
  if (player.isSimulated) return DATA_SOURCES.simulated;
  return DATA_SOURCES[player.provenance?.source] ?? DATA_SOURCES.espn;
}

export const VERIFIED_SOURCE_IDS = Object.freeze(
  Object.values(DATA_SOURCES)
    .filter((source) => source.verified)
    .map((source) => source.id)
);
